/**
 * The Help Centre's reader view of what the API sends, and its URLs. Pure, so
 * the pages, the breadcrumb, the search index and PR 5's editor preview all
 * apply the same rules.
 */
import {
	HelpFaqItem,
	HelpMarkdownPayload,
	HelpSection,
	HelpTopic,
	HelpVideo,
	HelpVideoFolder,
	ReaderSection,
	ReaderTopic,
} from "../../types/helpCentre.types";

export const HELP_CENTRE_PATH = "/help-centre";

/** URL parameters: the search query, a markdown tab (1-based) and an FAQ to open. */
export const QUERY_PARAM = "q";
export const TAB_PARAM = "tab";
export const FAQ_PARAM = "faq";

/** How markdown refers to an uploaded image: `![alt](help-asset:<id>)`. */
export const HELP_ASSET_PREFIX = "help-asset:";
const HELP_ASSET_SOURCE = /^help-asset:([0-9a-f]{32})$/;

/** The id of the uploaded image an image source refers to, or null. */
export const helpAssetIdOf = (src: string | undefined): string | null =>
	HELP_ASSET_SOURCE.exec(src ?? "")?.[1] ?? null;

/** A plain object, as JSON from the API should be. */
export const isRecord = (value: unknown): value is Record<string, any> =>
	!!value && typeof value === "object" && !Array.isArray(value);

export const isText = (value: unknown): value is string =>
	typeof value === "string";

const hasKey = (value: Record<string, any>) =>
	isText(value.key) && value.key.length > 0;

/** The objects in a list from the API; anything else is left out. */
export const records = (value: unknown): Record<string, any>[] =>
	Array.isArray(value) ? value.filter(isRecord) : [];

/** Questions with a key, question and answer; anything malformed is left out. */
export const toReaderFaqs = (value: unknown): HelpFaqItem[] =>
	records(value)
		.filter(
			(item) => hasKey(item) && isText(item.question) && isText(item.answer)
		)
		.map(({ key, question, answer }) => ({ key, question, answer }));

const toMarkdown = (value: unknown): HelpMarkdownPayload | null =>
	isRecord(value)
		? {
				tabMode: value.tabMode === true,
				markdownContent: isText(value.markdownContent)
					? value.markdownContent
					: "",
				markdownTabs: records(value.markdownTabs)
					.filter((tab) => isText(tab.title))
					.map((tab) => ({
						title: tab.title,
						content: isText(tab.content) ? tab.content : "",
					})),
			}
		: null;

const toVideos = (value: unknown): HelpVideo[] =>
	records(value)
		.filter((video) => isText(video.title) && isText(video.link))
		.map((video) => ({
			title: video.title,
			link: video.link,
			...(isText(video.description) && video.description
				? { description: video.description }
				: {}),
		}));

const toFolders = (value: unknown): HelpVideoFolder[] | null =>
	Array.isArray(value)
		? records(value)
				.filter((folder) => isText(folder.title))
				.map((folder) => ({
					title: folder.title,
					videos: toVideos(folder.videos),
				}))
		: null;

/**
 * A section with only the payload its `type` names, as the server's
 * `readerSection` sends it; null for an unknown type or a missing payload.
 * PR 5's preview can pass an editor section through this too.
 */
export const toReaderSection = (section: HelpSection): ReaderSection | null => {
	if (!isRecord(section) || !hasKey(section) || !isText(section.title)) {
		return null;
	}
	const base = {
		_id: String(section._id),
		key: section.key,
		title: section.title,
		isImportant: section.isImportant === true,
	};
	switch (section.type) {
		case "markdown": {
			const markdown = toMarkdown(section.markdown);
			return markdown && { ...base, type: "markdown", markdown };
		}
		case "faq":
			return Array.isArray(section.faq)
				? { ...base, type: "faq", faq: toReaderFaqs(section.faq) }
				: null;
		case "folder": {
			const folder = toFolders(section.folder);
			return folder && { ...base, type: "folder", folder };
		}
		default:
			return null;
	}
};

/**
 * What a reader sees of the topics the API sent: no hidden topics or
 * sections, and one payload per section. Super-admins are sent the editor
 * view, so this is the server's `readerTopic` rule again. The server's order
 * is kept, and nothing is sorted or changed in place.
 */
export const toReaderTopics = (topics: HelpTopic[]): ReaderTopic[] =>
	records(topics)
		.filter(
			(topic) => !topic.isHidden && hasKey(topic) && isText(topic.topicTitle)
		)
		.map((topic) => ({
			_id: String(topic._id),
			key: topic.key,
			topicTitle: topic.topicTitle,
			description: isText(topic.description) ? topic.description : "",
			icons: isText(topic.icons) ? topic.icons : undefined,
			isImportant: topic.isImportant === true,
			module_key: isText(topic.module_key) ? topic.module_key : undefined,
			children: records(topic.children)
				.filter((section) => !section.isHidden)
				.map((section) => toReaderSection(section as HelpSection))
				.filter((section): section is ReaderSection => section !== null),
		}));

/** The topics a tenant sees: a topic tagged with a module needs that subscription. */
export const availableTopics = (
	topics: ReaderTopic[],
	hasModule: (moduleKey: string) => boolean
): ReaderTopic[] =>
	topics.filter((topic) => !topic.module_key || hasModule(topic.module_key));

/** "1 guide", "3 guides": a topic's visible sections. */
export const guideCount = (topic: ReaderTopic): string =>
	`${topic.children.length} ${topic.children.length === 1 ? "guide" : "guides"}`;

interface HelpView {
	/** A markdown tab to select, 1-based. */
	tab?: number;
	/** An FAQ item to open. */
	faq?: string;
}

const viewQuery = ({ tab, faq }: HelpView): string => {
	const params = new URLSearchParams();
	if (tab) params.set(TAB_PARAM, String(tab));
	if (faq) params.set(FAQ_PARAM, faq);
	const query = params.toString();
	return query ? `?${query}` : "";
};

export const helpTopicsPath = (): string => `${HELP_CENTRE_PATH}/topics`;

/** A topic, or one of its sections, optionally with a tab to select or an FAQ to open. */
export const helpTopicPath = (
	topicKey: string,
	sectionKey?: string,
	view: HelpView = {}
): string =>
	`${helpTopicsPath()}/${encodeURIComponent(topicKey)}` +
	(sectionKey ? `/${encodeURIComponent(sectionKey)}` : "") +
	viewQuery(view);

/** The landing page with one of the default FAQs open. */
export const helpFaqPath = (faqKey: string): string =>
	`${HELP_CENTRE_PATH}${viewQuery({ faq: faqKey })}`;

/** The configuration page's parameters: a topic, and a section in it, to open in the editor. */
export const EDIT_TOPIC_PARAM = "topic";
export const EDIT_SECTION_PARAM = "section";

/** The configuration page, optionally opening a topic's editor at one of its sections. */
export const helpConfigurationPath = (
	topicKey?: string,
	sectionKey?: string
): string => {
	const params = new URLSearchParams();
	if (topicKey) {
		params.set(EDIT_TOPIC_PARAM, topicKey);
		if (sectionKey) params.set(EDIT_SECTION_PARAM, sectionKey);
	}
	const query = params.toString();
	const path = `${HELP_CENTRE_PATH}/configuration`;
	return query ? `${path}?${query}` : path;
};

interface Ordered {
	isImportant?: boolean;
	sequence?: number;
	key?: string;
}

/**
 * Featured first, then `sequence`, then `key`: the server's display order
 * (`sortForDisplay` in its `lib/helpContent/content.ts`), so the editor can
 * keep the cache in that order after a write. Returns a new array.
 */
export const sortForDisplay = <T extends Ordered>(items: T[]): T[] =>
	[...items].sort((a, b) => {
		const featured = Number(!!b.isImportant) - Number(!!a.isImportant);
		if (featured) return featured;
		const sequence = (a.sequence ?? 0) - (b.sequence ?? 0);
		if (sequence) return sequence;
		const aKey = a.key ?? "";
		const bKey = b.key ?? "";
		if (aKey === bKey) return 0;
		return aKey < bKey ? -1 : 1;
	});

/**
 * The breadcrumb's name for the Help Centre path segment at `index`, where
 * `segments` is the path split on "/" (`["", "help-centre", "topics", topic,
 * section]`). Topic and section titles come from `topics`; undefined when
 * those haven't loaded or don't include it.
 */
export const helpCrumbTitle = (
	segments: string[],
	index: number,
	topics?: ReaderTopic[]
): string | undefined => {
	if (index === 0) return "Help Centre";
	if (index === 1) return segments[2] === "topics" ? "Topics" : undefined;
	const topic = topics?.find((item) => item.key === segments[3]);
	if (index === 2) return topic?.topicTitle;
	if (index === 3) {
		return topic?.children.find((section) => section.key === segments[4])
			?.title;
	}
	return undefined;
};

/** The 0-based tab a `?tab=` value selects; the first tab for anything out of range. */
export const tabIndexFromParam = (
	value: string | null,
	tabCount: number
): number => {
	const tab = Number(value);
	return Number.isInteger(tab) && tab >= 1 && tab <= tabCount ? tab - 1 : 0;
};
